import React, { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import axios from "axios";
import InputField from "components/fields/InputField";
import SubmitButton from "components/SubmitButton/SubmitButton";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import { FaMailBulk, FaPhone, FaWhatsapp } from "react-icons/fa";
import userResources from "api/userResources";
import { toast } from "react-toastify";
import Select from "react-tailwindcss-select";
import getSelectClassNames from "components/SelectFieldClasses/SelectFieldClasses";
import { MaterialReactTable } from "material-react-table";
import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import DateFilter from "components/DateFilter/DateFilter";
import { Edit } from "@mui/icons-material";
import ConfirmModal from "components/ConfirmModal/ConfirmModal";
import { selectUserInfo } from "../../../redux/userSlice";
import { useSelector } from "react-redux";
import SelectFilter from "components/SelectFilter/SelectFilter";

export default function CreateNewCustomer() {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    clearErrors,
    control,
    formState: { errors },
  } = useForm();

  // tabel state
  const userInfo = useSelector(selectUserInfo);
  const [customerData, setCustomerData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [isError, setIsError] = useState();
  const [columnsFilterValue, setColumnsFilterValue] = useState([]);
  const [updatedFilterDate, setUpdatedFilterDate] = useState("");
  const [createdAtFilterDate, setCreatedAtFilterDate] = useState("");
  const [dob, setDob] = useState("");
  const [countryFilter, setCountryFilter] = useState();
  const [countryFilterData, setCountryFilterData] = useState([]);
  const [stateFilter, setStateFilter] = useState();
  const [stateFilterData, setStateFilterData] = useState([]);
  const [cityFilter, setCityFilter] = useState();
  const [cityFilterData, setCityFilterData] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({});

  // form state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [altphoneNumber, setAltPhoneNumber] = useState("");
  const [counties, setCounties] = useState([]);
  const [state, setState] = useState([]);
  const [city, setCity] = useState([]);
  const countryId = useWatch({ control, name: "country_id" });
  const stateId = useWatch({ control, name: "state_id" });

  // form start
  const onSubmit = async (data) => {
    setDisabled(true); // Disable button
    const toastId = toast.loading("Processing your request...");

    try {
      const formattedData = {
        ...data,
        phone_number: phoneNumber,
        alternate_phone: altphoneNumber,
        // If editingCustomer exists, include its id
        ...(editingCustomer && { id: editingCustomer.id }),
      };

      // You might consider renaming this function (or calling a different one) if the purpose is to update the customer when editingCustomer exists.
      await userResources.createNewCustomer(formattedData);

      toast.update(toastId, {
        render: editingCustomer
          ? "Customer updated successfully!"
          : "Customer added successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      // Reset filters and get updated customers
      setFilters({});
      setCountryFilter(null);
      setStateFilter(null);
      setCityFilter(null);
      getCustomer(
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
        filters
      );

      // Reset the form and phone numbers
      reset();
      setAltPhoneNumber(null);
      setPhoneNumber(null);
    } catch (error) {
      console.error("Error processing customer:", error);

      // Extract a custom error message from the server response if available
      let errorMessage = editingCustomer
        ? "Failed to update customer. Please try again."
        : "Failed to add customer. Please try again.";

      // Check if error object has response info (common in libraries like Axios)
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Fallback to a generic error message provided by the error object
        errorMessage = error.message;
      }

      toast.update(toastId, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setDisabled(false); // Re-enable button
    }
  };

  const getCountries = async () => {
    try {
      const response = await userResources.countries();
      const combinedData = response.data.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
      // setCountryFilterData(response.data.data);

      setCounties(combinedData);
    } catch (error) {
      console.log(error);
    }
  };

  const getCountriesForFilter = async () => {
    try {
      const response = await userResources.countries();
      setCountryFilterData(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getState = async (countryId) => {
    try {
      const response = await userResources.state(countryId);
      const combinedData = response.data.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
      // setStateFilterData(response.data.data);

      setState(combinedData);
    } catch (error) {
      console.log(error);
    }
  };
  const getStateForFilter = async (countryId) => {
    try {
      const response = await userResources.state(countryId);
      setStateFilterData(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getCity = async (params) => {
    try {
      const response = await userResources.city(params);

      const combinedData = response.data.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
      setCity(combinedData);
    } catch (error) {
      console.log(error);
    }
  };

  const getCityForFilter = async (params) => {
    try {
      const response = await userResources.city(params);
      setCityFilterData(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    getCountries();
    getCountriesForFilter();
  }, []);

  useEffect(() => {
    setState([]);
    setCity([]);

    if (countryId) {
      getState(countryId);
    }
  }, [countryId]);

  useEffect(() => {
    setCity([]);
    if (countryId && stateId) {
      getCity({ country: countryId, state: stateId });
    }
  }, [stateId]);

  // form end

  const getCustomer = async (pageIndex, pageSize, globalFilter, filter) => {
    setIsLoading(true);
    try {
      // Construct the params object
      const params = {
        page: pageIndex + 1, // Convert to 1-based index
        limit: pageSize,
        ...(globalFilter && { email: globalFilter }), // Add global filter if present
        ...filter, // Spread the filter object
      };

      // Call the customer API with the params object
      const response = await userResources.customerList(params);

      // Update state with the response data
      setCustomerData(response.data.data);
      setTotalRows(response.data.paginate_data.total);
    } catch (error) {
      console.error("Error fetching customer data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const customerColumns = React.useMemo(
    () => [
      {
        accessorKey: "full_name",
        header: "Full Name",
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "email",
        header: "Email Address",
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "phone_number",
        header: "Phone Number",
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "alternate_phone",
        header: "Alternate Phone",
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "address",
        header: "Address",
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "dob",
        header: "Date of Birth",
        filterVariant: "select",
        enableColumnFilter: true,
        Filter: ({ column }) => (
          <DateFilter
            column={column}
            selectedDate={dob}
            setSelectedDate={setDob}
          />
        ),
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "N/A";
          const createdAt = new Date(value);
          const date = createdAt.toLocaleDateString();
          const time = createdAt.toLocaleTimeString();
          return (
            <div className="flex flex-col">
              <p className="font-semibold">
                Date: <span className="font-light">{date}</span>
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "gender",
        header: "Gender",
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "N/A";
          return value.charAt(0).toUpperCase() + value.slice(1);
        },
      },
      {
        accessorKey: "languages",
        header: "Languages",
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "medical_information",
        header: "Medical Information",
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "passport_number",
        header: "Passport Number",
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "postal_code",
        header: "Postal/Zip Code",
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "countryName",
        header: "Country",
        Cell: ({ cell }) => cell.getValue()?.name || "N/A",
        Filter: ({ column }) => (
          <SelectFilter
            options={countryFilterData}
            selectedId={countryFilter}
            setSelectedId={setCountryFilter}
            column={column}
            placeholder="Select Country..."
          />
        ),
      },
      {
        accessorKey: "stateName",
        header: "Province/State",
        Cell: ({ cell }) => cell.getValue()?.name || "N/A",
        Filter: ({ column }) => (
          <SelectFilter
            options={stateFilterData}
            selectedId={stateFilter}
            setSelectedId={setStateFilter}
            column={column}
            placeholder="Select Country..."
            disabled={countryFilter ? false : true}
          />
        ),
      },
      {
        accessorKey: "cityName",
        header: "City",
        Cell: ({ cell }) => cell.getValue()?.name || "N/A",
        Filter: ({ column }) => (
          <SelectFilter
            options={cityFilterData}
            selectedId={cityFilter}
            setSelectedId={setCityFilter}
            column={column}
            placeholder="Select Country..."
            disabled={stateFilter ? false : true}
          />
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created Date",
        filterVariant: "select",
        enableColumnFilter: true,
        Filter: ({ column }) => (
          <DateFilter
            column={column}
            selectedDate={createdAtFilterDate}
            setSelectedDate={setCreatedAtFilterDate}
          />
        ),
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "N/A";
          const createdAt = new Date(value);
          const date = createdAt.toLocaleDateString();
          const time = createdAt.toLocaleTimeString();
          return (
            <div className="flex flex-col">
              <p className="font-semibold">
                Date: <span className="font-light">{date}</span>
              </p>
              <p className="font-semibold">
                Time: <span className="font-light">{time}</span>
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "updated_at",
        header: "Updated Date",
        enableColumnFilter: true,
        Filter: ({ column }) => (
          <DateFilter
            column={column}
            selectedDate={updatedFilterDate}
            setSelectedDate={setUpdatedFilterDate}
          />
        ),
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "N/A";
          const updatedAt = new Date(value);
          const date = updatedAt.toLocaleDateString();
          const time = updatedAt.toLocaleTimeString();
          return (
            <div className="flex flex-col">
              <p className="font-semibold">
                Date: <span className="font-light">{date}</span>
              </p>
              <p className="font-semibold">
                Time: <span className="font-light">{time}</span>
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "action",
        header: "Action",
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <div className="flex space-x-2">
            <Edit
              className="cursor-pointer text-blue-500"
              onClick={() => handleEdit(row.original)}
            />
            {userInfo?.data?.role_id ===
              Number(process.env.REACT_APP_ROLE_SUPERADMIN) ||
            userInfo?.data?.role_id ===
              Number(process.env.REACT_APP_ROLE_ADMIN) ? (
              <ConfirmModal
                onConfirm={handleDelete}
                title="Delete Customer"
                message="Are you sure you want to delete this customer?"
                employeeId={row.original.id}
              />
            ) : null}
          </div>
        ),
      },
    ],
    [filters, countryFilterData, stateFilterData, cityFilterData]
  );

  const handleEdit = (customer) => {
    // Populate form fields using setValue from useForm
    setValue("full_name", customer.full_name || "");
    setValue("email", customer.email || "");
    setValue("phone_number", customer.phone_number || "");
    setValue("alternate_phone", customer.alternate_phone || ""); // Adjust the key if needed
    setValue("address", customer.address || "");
    setValue("dob", customer.dob || "");
    setValue("gender", customer.gender || "");
    setValue("country_id", customer.country_id || "");
    setValue("state_id", customer.state_id || "");
    setValue("city_id", customer.city_id || "");
    setValue("languages", customer.languages || "");
    setValue("medical_information", customer.medical_information || "");
    setValue("postal_code", customer.postal_code || "");
    setValue("passport_number", customer.passport_number || "");
    setPhoneNumber(customer.phone_number);
    setAltPhoneNumber(customer.alternate_phone);

    // Update editing state if needed
    setEditingCustomer(customer);

    // Scroll to the top of the page smoothly
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setEditingCustomer(null);
    reset();
  };
  const handleDelete = async (id) => {
    const loadingToastId = toast.loading("Deleting customer...");
    try {
      await userResources.customerDelete(id);
      toast.update(loadingToastId, {
        render: "Customer deleted successfully.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      setFilters({});
      setCountryFilter(null);
      setStateFilter(null);
      setCityFilter(null);
      getCustomer(
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
        filters
      );
    } catch (error) {
      console.error("Error deleting customer:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to delete customer. Please try again.";
      toast.update(loadingToastId, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const handlePaginationChange = (updatedPagination) => {
    setPagination(updatedPagination);
  };
  const handleGlobalFilterChange = (value) => {
    setGlobalFilter(value);
  };
  // Updated handleFilterValues function
  const handleFilterValues = () => {
    const updatedFilters = {};

    columnsFilterValue.forEach((key) => {
      if (
        key.id !== "countryName" &&
        key.id !== "stateName" &&
        key.id !== "cityName" &&
        key.value !== null
      ) {
        updatedFilters[key.id] = key.value;
      }
    });

    // Add country_id, state_id, and city_id separately
    if (countryFilter) {
      updatedFilters["country_id"] = countryFilter;
    }
    if (stateFilter) {
      updatedFilters["state_id"] = stateFilter;
    }
    if (cityFilter) {
      updatedFilters["city_id"] = cityFilter;
    }

    setFilters(updatedFilters);
  };

  useEffect(() => {
    handleFilterValues();
  }, [columnsFilterValue]);

  useEffect(() => {
    getCustomer(
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
      filters
    );
  }, [pagination, globalFilter, filters]);
  useEffect(() => {
    setStateFilterData([]);
    setCityFilterData([]);

    if (countryFilter) {
      getStateForFilter(countryFilter);
    }
  }, [countryFilter]);

  useEffect(() => {
    setCityFilterData([]);
    if (countryFilter && stateFilter) {
      getCityForFilter({ country: countryFilter, state: stateFilter });
    }
  }, [stateFilter]);
  return (
    <section className="mt-6">
      <ScrollToTopOnRouteChange />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-1">
        <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <h3 className="mb-4 text-xl font-semibold dark:text-white">
              {editingCustomer ? "Edit Customer" : "Add Customer"}
            </h3>
            {editingCustomer && (
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-lg border border-red-500 p-2 text-red-500 hover:bg-red-500 hover:text-white focus:outline-none"
              >
                Cancel
              </button>
            )}
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-2 md:grid-cols-3">
              <div>
                <InputField
                  label="Full Name"
                  id="full_name"
                  name="full_name"
                  placeholder="Enter full name"
                  register={register}
                  rules={{
                    required: "Full Name is required",
                  }}
                  errorMessage={errors.full_name?.message}
                />
              </div>

              <div>
                <InputField
                  label="Email Address"
                  id="email"
                  name="email"
                  placeholder="Enter email address"
                  register={register}
                  // rules={{
                  //   required: "Email is required",
                  //   pattern: {
                  //     value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  //     message: "Invalid email address",
                  //   },
                  // }}
                  // errorMessage={errors.email?.message}
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="my-2 block text-sm font-medium text-gray-900 dark:text-[#ff4e41]"
                >
                  Phone Number
                </label>
                <Controller
                  name="phone_number"
                  control={control}
                  // rules={{ required: "Phone number is required" }}
                  render={({ field }) => (
                    <div className="relative">
                      <div className="flex">
                        <PhoneInputWithCountrySelect
                          defaultCountry="CA"
                          value={field.value || ""}
                          onChange={(value) => {
                            field.onChange(value);
                            setPhoneNumber(value);
                          }}
                          placeholder="Enter Phone Number"
                          className="h-10 w-full rounded-md border px-2 text-lg focus:outline-none focus:ring-2 dark:!border-white/10"
                        />
                      </div>
                      <div className="absolute right-4 top-4">
                        <FaPhone />
                      </div>
                      {errors.phone_number && (
                        <span className="text-sm text-red-600">
                          {errors.phone_number.message}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="my-2 block text-sm font-medium text-gray-900 dark:text-[#ff4e41]"
                >
                  Alternate Phone Number
                </label>
                <div className="relative">
                  <div className="flex">
                    <PhoneInputWithCountrySelect
                      defaultCountry="CA"
                      value={altphoneNumber}
                      onChange={(value) => {
                        setAltPhoneNumber(value);
                        setValue("alternate_phone", value); // Update the useForm state
                      }}
                      placeholder="Enter Alternate Phone Number"
                      className="h-10 w-full rounded-md border px-2 text-lg focus:outline-none focus:ring-2 dark:!border-white/10"
                    />
                  </div>
                  <div className="absolute right-4 top-4">
                    <FaPhone />
                  </div>
                </div>
              </div>

              <div>
                <InputField
                  label="Address"
                  id="address"
                  name="address"
                  placeholder="Enter address"
                  register={register}
                  // rules={{
                  //   required: "Address is required",
                  // }}
                  // errorMessage={errors.address?.message}
                />
              </div>

              <div>
                <InputField
                  label="Date of Birth"
                  id="dob"
                  name="dob"
                  type="date"
                  register={register}
                />
              </div>

              <div>
                <label className="my-2 block text-sm font-medium text-gray-900 dark:text-[#ff4e41]">
                  Gender
                </label>
                <div className="flex items-center gap-4 dark:text-white">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="male"
                      {...register("gender")}
                      className="mr-2"
                    />
                    Male
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="female"
                      {...register("gender")}
                      className="mr-2"
                    />
                    Female
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="other"
                      {...register("gender")}
                      className="mr-2"
                    />
                    Other
                  </label>
                </div>
              </div>
              <div>
                <label className="my-2 block text-sm font-medium text-gray-900 dark:text-[#ff4e41]">
                  Country
                </label>
                <Select
                  {...register(`country_id`)}
                  value={counties.find(
                    (source) => source.value === watch("country_id") // Use watch to get the current value
                  )} // Controlled value
                  onChange={(selectedOption) => {
                    setValue("country_id", selectedOption?.value || ""); // Update handler using setValue from React Hook Form
                    clearErrors("country_id");
                  }}
                  options={counties} // Format options
                  isSearchable={true} // Enable search functionality
                  placeholder="Select..."
                  classNames={getSelectClassNames({
                    isDisabled: false,
                    selectProps: {},
                    isSelected: false,
                    value: watch("country_id"), // Use watch to get the current value
                  })} // Pass parameters here
                  formatOptionLabel={(data) => (
                    <li
                      className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                        !data.isSelected
                          ? `bg-gray-200 text-gray-900`
                          : `bg-blue-800 text-gray-200`
                      }`}
                    >
                      {data.label}
                    </li>
                  )}
                />
              </div>
              <div>
                <label className="my-2 block text-sm font-medium text-gray-900 dark:text-[#ff4e41]">
                  State
                </label>
                <Select
                  {...register(`state_id`)}
                  value={state.find(
                    (source) => source.value === watch("state_id") // Use watch to get the current value
                  )} // Controlled value
                  onChange={(selectedOption) => {
                    setValue("state_id", selectedOption?.value || ""); // Update handler using setValue from React Hook Form
                    clearErrors("state_id");
                  }}
                  options={state} // Format options
                  isSearchable={true} // Enable search functionality
                  placeholder="Select..."
                  isDisabled={watch("country_id") ? false : true}
                  classNames={getSelectClassNames({
                    isDisabled: watch("country_id") ? false : true,
                    selectProps: {},
                    isSelected: false,
                    value: watch("state_id"), // Use watch to get the current value
                  })} // Pass parameters here
                  formatOptionLabel={(data) => (
                    <li
                      className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                        !data.isSelected
                          ? `bg-gray-200 text-gray-900`
                          : `bg-blue-800 text-gray-200`
                      }`}
                    >
                      {data.label}
                    </li>
                  )}
                />
              </div>
              <div>
                <label className="my-2 block text-sm font-medium text-gray-900 dark:text-[#ff4e41]">
                  City
                </label>
                <Select
                  {...register(`city_id`)}
                  value={city.find(
                    (source) => source.value === watch("city_id") // Use watch to get the current value
                  )} // Controlled value
                  onChange={(selectedOption) => {
                    setValue("city_id", selectedOption?.value || ""); // Update handler using setValue from React Hook Form
                    clearErrors("city_id");
                  }}
                  options={city} // Format options
                  isSearchable={true} // Enable search functionality
                  placeholder="Select..."
                  isDisabled={watch("state_id") ? false : true}
                  classNames={getSelectClassNames({
                    isDisabled: watch("state_id") ? false : true,
                    selectProps: {},
                    isSelected: false,
                    value: watch("city_id"), // Use watch to get the current value
                  })} // Pass parameters here
                  formatOptionLabel={(data) => (
                    <li
                      className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                        !data.isSelected
                          ? `bg-gray-200 text-gray-900`
                          : `bg-blue-800 text-gray-200`
                      }`}
                    >
                      {data.label}
                    </li>
                  )}
                />
              </div>
              <div>
                <InputField
                  label="Preferred Language"
                  id="languages"
                  name="languages"
                  placeholder="Hindi,English"
                  register={register}
                  // rules={{
                  //   required: "Languages are required",
                  //   validate: (value) => {
                  //     const languagesArray = value
                  //       .split(",")
                  //       .map((lang) => lang.trim())
                  //       .filter((lang) => lang.length > 0);
                  //     return (
                  //       languagesArray.length > 0 ||
                  //       "At least one language is required"
                  //     );
                  //   },
                  // }}
                  // errorMessage={errors.languages?.message}
                />
              </div>
              <div>
                <InputField
                  label="Medical Information"
                  id="medical_information"
                  name="medical_information"
                  placeholder="Enter any relevant medical information (optional)"
                  register={register}
                />
              </div>

              <div>
                <InputField
                  label="	Postal/Zip Code"
                  id="postal_code"
                  name="postal_code"
                  placeholder="Enter Postal/Zip Code (optional)"
                  register={register}
                />
              </div>
              <div>
                <InputField
                  label="Passport Number"
                  id="passport_number"
                  name="passport_number"
                  placeholder="Enter passport number (optional)"
                  register={register}
                />
              </div>
              <div className="">
                <SubmitButton
                  editing={editingCustomer}
                  disabled={disabled}
                  add="Submit"
                  edit="Update"
                  extraClass="btn w-full border-0"
                />
              </div>
            </div>
          </form>
        </div>
        <MaterialReactTable
          columns={customerColumns}
          data={customerData}
          state={{
            pagination,
            isLoading,
            showProgressBars: isLoading,
            showAlertBanner: isError,
            // columnFilters: columnsFilterValue,
          }} // Use both states here
          muiToolbarAlertBannerProps={
            isError
              ? {
                  color: "error",
                  children: "Error loading data",
                }
              : undefined
          }
          enableColumnFilters={true} // Pass the current visibility state
          initialState={{
            showColumnFilters: true,
          }}
          onColumnFiltersChange={setColumnsFilterValue}
          enableSorting={false}
          enableColumnOrdering={false}
          enableRowActions
          enablePagination
          onPaginationChange={handlePaginationChange}
          manualPagination
          rowCount={totalRows}
          pageCount={Math.ceil(totalRows / pagination.pageSize)}
          isLoading={isLoading}
          muiTablePaginationProps={{
            showFirstButton: true,
            showLastButton: true,
          }}
          enableGlobalFilter // Keep this true to allow global filtering
          onGlobalFilterChange={handleGlobalFilterChange} // Update the state on filter change
          globalFilter={globalFilter} // Pass the state variable to the table
          // renderTopToolbarCustomActions={({ table }) => (
          //   <div className="relative top-2 flex items-center gap-2">
          //     <IoReloadOutline
          //       size={25}
          //       className={`cursor-pointer font-semibold hover:text-green-500 ${
          //         isLoading ? "animate-spin" : ""
          //       }`}
          //       onClick={() => {
          //         setColumnsFilterValue([]); // Reset all filters
          //         setGlobalFilter(""); // Reset global filter
          //         getCustomer(
          //           pagination.pageIndex,
          //           pagination.pageSize,
          //           "",
          //           {}
          //         ); // Refetch data without filters
          //       }}
          //     />
          //   </div>
          // )}
          // editingMode="modal" // Ensure this is correctly set
          // enableEditing // This should also be true
          renderRowActions={({ row }) => (
            <div className="flex flex-nowrap gap-4">
              <button
                className="text-xl text-primary"
                onClick={() =>
                  window.open(
                    `mailto:${row.original.email}?subject=Hello ${row.original.full_name}!`
                  )
                }
              >
                <FaMailBulk />
              </button>
              <button
                className="text-xl text-success"
                onClick={() => {
                  const phone =
                    row.original.phone_number || row.original.alternate_phone;
                  if (phone) {
                    window.open(
                      `https://wa.me/${phone.replace(/\D/g, "")}?text=Hello ${
                        row.original.full_name
                      }!`
                    );
                  }
                }}
              >
                <FaWhatsapp />
              </button>
            </div>
          )}
        />
      </div>
    </section>
  );
}
